import { Form } from '@ant-design/compatible';
import { Alert, Col, Row, Typography } from 'antd';
import type { FormikProps } from 'formik';
import moment from 'moment-timezone';
import React from 'react';

import { convertSecondsToLargestUnit } from '../../../components/DurationField/helpers';
import { TimetableField } from '../../../components/TimetableField';
import { UserTimezoneSelect } from '../../../components/UserTimezoneSelect';
import { PermissionKey_enum, PermissionScope_enum } from '../../../graphql/hasura/generated';

import { DurationOfAccessDatePicker } from './DurationOfAccessDatePicker';
import { ISharedFormValues } from './formik';
import { getRoleRequirements } from './getRoleRequirements';

type DurationOfAccessSectionProps = FormikProps<ISharedFormValues> & {
  scheduleAllowed?: boolean;
};

export const DurationOfAccessSection = (props: DurationOfAccessSectionProps) => {
  const { setFieldValue, values, errors: formikErrors } = props;
  const { scopedIds, role, activatesAt, expiresAt } = values;

  const { expirationDateRequired, maxAccessDurationSeconds } = getRoleRequirements(role);

  if (!scopedIds.length) {
    return null;
  }

  const rolePermissions = role?.rolePermissions || [];

  let scheduleAllowed = props.scheduleAllowed;

  if (scheduleAllowed !== false) {
    scheduleAllowed = (
      values.permissionScope === PermissionScope_enum.PROPERTY ||
      values.permissionScope === PermissionScope_enum.UNIT ||
      values.permissionScope === PermissionScope_enum.ACCESS_POINT
    ) && (
      rolePermissions.some(rp => rp.permission.key === PermissionKey_enum.AccessPoint_Unlock) ||
      rolePermissions.some(rp => rp.permission.key === PermissionKey_enum.SmartLock_Unlock)
    );
  }

  return (
    <section style={{ marginBottom: 20 }}>
      <Typography.Title level={4}>
        Duration of access
      </Typography.Title>

      <Typography.Paragraph>
        Please select the dates the user's access should be activated and expired (if necessary).
      </Typography.Paragraph>
      <Form.Item
        validateStatus={formikErrors.selectedTimezone ? 'error' : undefined}
        help={formikErrors.selectedTimezone}
        label='Timezone'
        style={{ width: '100%' }}
        className='label-align-top'
      >
        <UserTimezoneSelect
          value={values.selectedTimezone}
          onChange={value => setFieldValue('selectedTimezone', value)}
          contextualTimezone={{
            value: values.propertyTimezone,
            formatLabel: (label) => (
              <>{label} — <strong>Selected property timezone</strong></>
            ),
          }}
          style={{ width: '100% '}}
        />
      </Form.Item>
      {values.propertyTimezoneWarning && (
        <Alert
          showIcon
          type='warning'
          message={values.propertyTimezoneWarning}
          style={{ marginBottom: 30 }}
        />
      )}
      <Row justify='space-between' gutter={.5}>
        <Col span={11}>
          <Form.Item
            required
            validateStatus={formikErrors.activatesAt ? 'error' : undefined}
            help={formikErrors.activatesAt}
            label='Activation Date'
            style={{ width: '100%' }}
            className='label-align-top'
          >
            <DurationOfAccessDatePicker
              fieldName='activatesAt'
              formikProps={props}
              datePickerProps={{
                placeholder: 'Activation Date',
                value: !!activatesAt ? moment(activatesAt).tz(values.selectedTimezone) : undefined,
                disabledDate: d => (
                  d?.isBefore(moment(new Date()).tz(values.selectedTimezone).startOf('day')) || false
                ),
              }}
            />
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            required={expirationDateRequired}
            validateStatus={formikErrors.expiresAt ? 'error' : undefined}
            help={formikErrors.expiresAt}
            label={`Expiration Date${expirationDateRequired ? '' : ' (Optional)'}`}
            className='label-align-top'
          >
            <DurationOfAccessDatePicker
              fieldName='expiresAt'
              formikProps={props}
              datePickerProps={{
                placeholder: 'Expiration Date',
                value: !!expiresAt ? moment(expiresAt).tz(values.selectedTimezone) : undefined,
                disabledDate: d => (
                  d?.isBefore(moment(activatesAt || new Date()).tz(values.selectedTimezone).startOf('day')) || false
                ),
                allowClear: !expirationDateRequired,
              }}
            />
            {typeof maxAccessDurationSeconds === 'number' && (
              <span style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.45)' }}>
                Maximum Duration:&nbsp;
                {convertSecondsToLargestUnit(maxAccessDurationSeconds).displayedValue}
              </span>
            )}
          </Form.Item>
        </Col>
      </Row>
      {scheduleAllowed && (
        <>
          <Typography.Paragraph>
            Optionally restrict access with schedules.
          </Typography.Paragraph>

          {rolePermissions.some(r => r.permission.key === PermissionKey_enum.Session_AdminPanel) && (
            <Typography.Paragraph>
              (Only applies to unlocking property access points and/or unit smart locks.
              Does not apply to Admin Panel access.)
            </Typography.Paragraph>
          )}

          <Row justify='space-between' gutter={.5}>
            <Col span={24}>
              <TimetableField
                fieldName='schedule'
                values={values}
                onChange={(value) => {
                  setFieldValue('schedule', value, true);
                }}
                modelName='UserRole'
                errors={formikErrors}
                // The form value needs to be "touched" for the errors to be displayed, but this
                // won't happen until the form is actually submitted. As a workaround to display
                // the errors after the user checks the "acknowledged" box, we can pass the errors
                // object to this component instead.
                touched={values.acknowledged ? formikErrors : {}}
              />
            </Col>
          </Row>
        </>
      )}
    </section>
  );
};
